// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useSelector } from 'APP/Hooks/redux'

import Analytics from 'APP/Services/Analytics'
import { navigationRef as Nav } from 'APP/Nav'

import TodayHeader from 'APP/Components/TodayHeader'
import RecentCarePlans from 'APP/Components/RecentCarePlans'
import UnreadMessages from 'APP/Components/UnreadMessages'
import PromotedContent from 'APP/Components/PromotedContent'
import AppLaunchpad from 'APP/Components/AppLaunchpad'
import Habits from 'APP/Containers/Habits'
import GetCareScreen from 'APP/Containers/GetCareScreen'
import InboxScreen from 'APP/Containers/InboxScreen'
import CarePlansScreen from 'APP/Containers/CarePlansScreen'
import IcbtPortalScreen from 'APP/Containers/IcbtPortalScreen'
import CmsFeedScreen from 'APP/Containers/CmsFeedScreen'
import ChallengesScreen from 'APP/Containers/ChallengesScreen'
import MainAction from 'APP/Components/MainAction'
import JourneyCard from 'APP/Components/JourneyCard'
import InactiveChatInfoCard from 'APP/Components/InfoCard/InactiveChatInfoCard'
import DiscoverNewContent from 'APP/Components/DiscoverNewContent'
import UpcomingAppointments from 'APP/Components/UpcomingAppointments'
import TakeATour from 'APP/Components/TakeATour'

import { Images } from 'APP/Themes'

import { RootScreens } from 'APP/Lib/Enums'
import { getServiceGroupIcon } from 'APP/Lib/CtaInfoHelpers'
import { IconProps } from 'APP/Converse/Icon'
import Icon from 'APP/Converse/Icon'
import { getOneServiceGroup } from 'APP/Services/CMS'
import Config from 'APP/Config'
import { logger } from 'APP/Lib/Datadog'
import {
  Audio,
  CmsService,
  ContactInfo,
  Document,
  EmployeeBenefit,
  ExternalService,
  InternalService,
  RawAppExternalService,
  RawAppInternalService,
  RawAppServiceGroup,
  RawContactInfo,
  RawEmployeeBenefit,
  RawService,
  RawServiceGroupSection,
  ServiceFilter,
  ServiceGroup,
  ServiceGroups,
  ServiceGroupSection,
  Services,
  Video,
} from 'App/Store/Content/types'
import type { HealthResources, ServicesAndOptions } from 'APP/Store/Patient/types'

import { ContentType } from '../Store/Content/types'

/**
 * This is a mapping of the name field for AppTabScreen tabs to allow for easy specific tab navigation.
 * This is fairly brittle since these values can be changed in Contentful at any time, breaking this
 * functionality, so uh, don't do that
 */
export const TabScreens = {
  chat: {
    getCare: 'Get Care - Chat',
    inbox: 'Inbox - Chat',
    carePlans: 'Care Plans - Chat',
  },
  move: {
    workouts: 'Workouts - Move',
    challenges: 'Challenges - Move',
  },
  mind: {
    toolkits: 'Toolkits - Mind',
    workouts: 'Mind Workouts - Mind',
  },
}

// Indicate whether a component should render based on a service filter and the user's eligible services
export function shouldRenderComponent(
  serviceFilter: ServiceFilter | null,
  eligibleServices: ServicesAndOptions
): boolean {
  const comparator = ({ service_id }: CmsService): boolean =>
    eligibleServices?.[service_id] !== undefined

  switch (serviceFilter?.boolean_operator) {
    case 'AND':
      return serviceFilter?.services?.every(comparator) ?? true
    case 'OR':
      return serviceFilter?.services?.some(comparator) ?? true
    default:
      return true
  }
}

// Expose for testing
export function _appComponentMapperFactory(eligibleServices: ServicesAndOptions, carePlans) {
  const componentMapping = {
    TodayHeader,
    UnreadMessages,
    RecentCarePlans,
    PromotedContent,
    AppLaunchpad,
    Habits,
    ChallengesScreen,
    CmsFeedScreen,
    GetCareScreen,
    InboxScreen,
    CarePlansScreen,
    IcbtPortalScreen,
    MainAction,
    JourneyCard,
    InactiveChatInfoCard,
    DiscoverNewContent,
    UpcomingAppointments,
    TakeATour,
  }

  return (appComponent) => {
    let canRenderComponent
    const componentName = appComponent?.mobile_component_name
    const isEligible = shouldRenderComponent(appComponent?.service_filter, eligibleServices)

    switch (componentName) {
      case 'RecentCarePlans':
        canRenderComponent = carePlans?.length
        break
      default:
        canRenderComponent = isEligible
    }

    return canRenderComponent ? componentMapping[componentName] ?? null : null
  }
}

export function useAppComponentMapper() {
  const eligibleServices = useSelector((state) => state?.patient?.profile?.eligibleServices)
  const carePlans = useSelector((state) => state?.history?.completedCarePlans)

  const appComponentMapper = useMemo(
    () => _appComponentMapperFactory(eligibleServices, carePlans),
    [eligibleServices, carePlans]
  )

  return appComponentMapper
}

export function navigateToCms(
  item: Audio | Document | Video,
  buttonValue: string,
  analyticsName: string,
  sprig: boolean = false
): void {
  Analytics.trackEvent('button_click', { button_value: buttonValue })

  if ('audio' in item) {
    Nav.navigate('cmsAudioScreen', {
      id: item.id,
      title: item.title,
      services: item.service_filter?.services,
      showShareButton: true,
    })
  } else if ('video' in item) {
    Nav.navigate('cmsVideoScreen', {
      id: item.id,
      title: item.title,
      services: item.service_filter?.services,
      showShareButton: true,
    })
  } else {
    const name = `${analyticsName}: ${item.id}`
    Nav.navigate('cmsScreen', {
      id: item.id,
      locale: item.locale,
      analyticsName: name,
      screenUri: item.title,
      services: item.service_filter?.services,
      sprig,
      showShareButton: true,
    })
  }
}

const _normalizeContactInfo = (contactInfo: RawContactInfo | null): ContactInfo | null => {
  if (!contactInfo) {
    return contactInfo
  }
  return {
    id: contactInfo.id,
    contentType: contactInfo.content_type,
    name: contactInfo.name,
    title: contactInfo.title,
    content: contactInfo.content,
    cardText: contactInfo.card_text,
    cardCallToAction: contactInfo.card_call_to_action,
    navigationUrl: contactInfo.navigation_url,
    screenName: contactInfo.screen_name,
    actionType: contactInfo.action_type,
  }
}

export const normalizeServiceV2 = (
  service: RawService | RawEmployeeBenefit
): InternalService | ExternalService | EmployeeBenefit => {
  const normalizedService = {
    id: service.id,
    name: service.name,
    title: service.title,
    browseType: service.browse_type,
    icon: service.icon,
  }

  switch (service.content_type) {
    case 'appExternalService':
      return {
        ...normalizedService,
        subtitle: service.subtitle,
        appIconName: service.app_icon_name,
        actionType: service.action_type,
        actionValue: service.action_value,
        browseTitle: service.browse_title,
        contactInfo: _normalizeContactInfo(service.contact_info),
        ctaLabel: service.cta_label,
        contentType: service.content_type,
        navigationUrl: service.navigation_url,
        providerIcon: service.provider_icon,
        providerTitle: service.provider_title,
        ...(service.service_groups
          ? {
              serviceGroups: service.service_groups.map(normalizeServiceGroup),
            }
          : { serviceGroups: null }),
      }
    case 'appInternalService':
      return {
        ...normalizedService,
        subtitle: service.subtitle,
        appIconName: service.app_icon_name,
        actionType: service.action_type,
        actionValue: service.action_value,
        browseTitle: service.browse_title,
        contactInfo: _normalizeContactInfo(service.contact_info),
        ctaLabel: service.cta_label,
        contentType: service.content_type,
        serviceFilter: service.service_filter,
      }
    case 'employeeBenefit':
      return {
        ...normalizedService,
        contentType: service.content_type,
        url: service.url,
      }
  }
}

export const normalizeServiceGroup = (serviceGroup: RawAppServiceGroup): ServiceGroup => ({
  id: serviceGroup.id,
  name: serviceGroup.name,
  title: serviceGroup.title,
  subtitle: serviceGroup.subtitle,
  contentType: serviceGroup.content_type,
  tileDescription: serviceGroup.tile_description,
  icon: serviceGroup.icon,
  appIconName: serviceGroup.app_icon_name,
  showEmergencyBanner: serviceGroup.show_emergency_services,
  showPrivacyPolicy: serviceGroup.show_privacy_policy,
  // @ts-expect-error although normalizeServiceV2 can return `EmployeeBenefit`s this will not happen, since these are never within the RawAppServiceGroup.services field
  services: serviceGroup.services?.map(normalizeServiceV2) || [],
  scopeOfPractice: serviceGroup.scope_of_practice,
  screenIconOverride: serviceGroup.screen_icon_override,
  displayServiceButtonCard: serviceGroup.display_service_button_card,
})

export const normalizeServiceGroupSection = (
  serviceGroupSection: RawServiceGroupSection
): ServiceGroupSection => ({
  name: serviceGroupSection.name,
  title: serviceGroupSection.title,
  serviceGroups: serviceGroupSection.items?.map(normalizeServiceGroup),
})

// Expose for testing
export const _allExternalServicesHaveSameProvider = (services: Services): boolean => {
  const hasServices = services.length > 0
  const providers = services.map((s) => 'providerTitle' in s && s?.providerTitle)
  // Ensure there is only one, truthy provider
  const haveSameProvider = new Set(providers).size === 1 && Boolean(providers[0])
  return hasServices && haveSameProvider
}

export function allServicesHaveSameProvider(services: Services): boolean {
  return services?.every(
    (s, _, arr) =>
      'providerTitle' in s && 'providerTitle' in arr[0] && s.providerTitle === arr[0].providerTitle
  )
}

export function someServicesHaveShowContactInfoActionType(services: Services): boolean {
  return services.some((s) => s.actionType === 'show_contact_info')
}

export function allServicesHaveSameContactInfoId(services: Services): boolean {
  return services?.every((s, _, arr) => s?.contactInfo?.id === arr[0]?.contactInfo?.id)
}

export const determineExternalProvider = (services: Services): string | null | undefined => {
  return _allExternalServicesHaveSameProvider(services) && 'providerTitle' in services[0]
    ? services[0].providerTitle
    : null
}

export function determineContactInfo(services: Services): ContactInfo | null {
  if (
    services.length === 0 ||
    someServicesHaveShowContactInfoActionType(services) ||
    !allServicesHaveSameContactInfoId(services)
  )
    return null

  return services[0].contactInfo || null
}

export const getServiceOrServiceGroupTileIcon = (
  serviceOrServiceGroup: InternalService | ExternalService | ServiceGroup,
  params
) => {
  const AppIcon = getServiceGroupIcon(serviceOrServiceGroup?.appIconName)
  const DefaultIcon = Images.serviceGroups.default

  if (AppIcon) {
    return <AppIcon {...params} />
  } else if (serviceOrServiceGroup?.icon?.url) {
    const iconParams: IconProps = {
      variant: 'svgUrl',
      src: serviceOrServiceGroup?.icon?.url,
      style: params?.style,
      color: 'accent',
    }
    // Only want to set `size` if not null, otherwise let Icon use its default `size`
    if (params?.width || params?.height) {
      iconParams['size'] = params?.width || params?.height
    }
    return <Icon {...iconParams} />
  } else {
    return <DefaultIcon {...params} />
  }
}

export function getCmsContentIds(eligibleHealthResources: HealthResources): string[] {
  return (
    eligibleHealthResources?.flatMap((item) => {
      if ('cmsContentId' in item.attributes && item?.attributes?.cmsContentId) {
        return item.attributes.cmsContentId
      } else if ('cmsContent' in item.attributes && item?.attributes?.cmsContent?.length > 0) {
        return item.attributes.cmsContent.map((content) => content?.cmsContentId)
      }
      return []
    }) ?? []
  )
}

// Get normalized app service group from CMS by id and locale
export async function getNormalizedServiceGroup(
  id: string,
  locale: string
): Promise<ServiceGroup | null> {
  try {
    const serviceGroup = await getOneServiceGroup(id, locale)
    return normalizeServiceGroup(serviceGroup)
  } catch (error) {
    logger.error('Failed to get normalized service group', { serviceGroupId: id, locale }, error)
    return null
  }
}

// Get normalized app service groups from CMS by ids and locale
export async function getNormalizedServiceGroups(
  ids: string[],
  locale: string
): Promise<ServiceGroups> {
  // @TODO: Optimize to fetch multiple service groups in one request if supported by CMS
  const normalizedServiceGroups = []

  for (const serviceGroupId of ids) {
    const normalizedServiceGroup = await getNormalizedServiceGroup(serviceGroupId, locale)
    if (normalizedServiceGroup) {
      normalizedServiceGroups.push(normalizedServiceGroup)
    }
  }
  return normalizedServiceGroups
}

// Indicate whether a component should render based on a user's eligible services and eligible health resources
// If the component is an appExternalService or EmployeeBenefit, render based on eligible health resources
// If the component has a serviceFilter defined (video, audio, content, document, appInternalService), render based on eligible services
export function shouldRenderCmsComponent(
  component:
    | Audio
    | Document
    | Video
    | RawAppInternalService
    | InternalService
    | RawAppExternalService
    | ExternalService
    | RawEmployeeBenefit
    | EmployeeBenefit,
  eligibleServices: ServicesAndOptions,
  eligibleHealthResources: HealthResources
): boolean {
  const contentType = 'contentType' in component ? component.contentType : component?.content_type

  let serviceFilter = null
  if ('serviceFilter' in component) {
    serviceFilter = component.serviceFilter
  } else if ('service_filter' in component) {
    serviceFilter = component.service_filter
  }

  if (contentType === ContentType.ExternalService || contentType === ContentType.EmployeeBenefit) {
    const cmsContentIds = getCmsContentIds(eligibleHealthResources)
    return cmsContentIds.includes(component?.id)
  } else {
    return shouldRenderComponent(serviceFilter, eligibleServices)
  }
}

// Filters a list of service groups and their associated services (internal and external)
// based on a user's eligible services and eligible healthResources
export function filterServiceGroupsAndServicesForEligibility(
  serviceGroups: ServiceGroups,
  eligibleServices: ServicesAndOptions,
  eligibleHealthResources: HealthResources
): ServiceGroups {
  return serviceGroups
    .map((serviceGroup) => {
      const services = serviceGroup?.services || []

      const filteredServices = services.filter((service) =>
        shouldRenderCmsComponent(service, eligibleServices, eligibleHealthResources)
      )

      return { ...serviceGroup, services: filteredServices }
    })
    .filter((serviceGroup) => serviceGroup.services.length > 0)
}

// TODO: deprecate this function and just use `item.content_type`
// @ts-expect-error will be deprecated, no need to type
export function getContentType(item) {
  if (item.video) return 'video'
  else if (item.audio) return 'audio'
  return undefined
}

/**
 * Replaces a placeholder value in a URL matching a key in the config file with its value.
 * If the placeholder is not found, returns the original URL.
 *
 * @param url - The URL containing a placeholder.
 * @returns The URL with the placeholder replaced or original if no placeholder found.
 */
export function replacePlaceholderURLTextWithConfig(url: string): URL | null {
  const replacedUrl = url.replace(/\{([A-Z0-9_]+)\}/gi, (_, key: string): any => {
    const value = Config[key]
    if (typeof value === 'undefined') {
      // Config placeholder not found - URL may be invalid
      return `{${key}}`
    }
    return value
  })

  try {
    return new URL(replacedUrl)
  } catch {
    return null
  }
}

export default {
  RootScreens,
  TabScreens,
  shouldRenderComponent,
  useAppComponentMapper,
  navigateToCms,
  normalizeServiceGroup,
  normalizeServiceGroupSection,
  shouldRenderCmsComponent,
  determineExternalProvider,
  determineContactInfo,
}
